"use client";

import { useEffect, useRef } from "react";
import { trackEvent } from "@/lib/analytics";

/**
 * Reports which offers actually rendered.
 *
 * Without an impression count, a click count is uninterpretable — it cannot
 * distinguish "this offer converts badly" from "this offer expired three
 * weeks ago and stopped rendering." It is also the only signal that surfaces
 * an offer silently dropped for a missing translation.
 *
 * Renders nothing. It exists so the hero itself can stay a Server Component.
 */
export function HeroImpression({
  offerIds,
  locale,
}: {
  offerIds: string[];
  locale: string;
}) {
  const sent = useRef(false);
  useEffect(() => {
    // The [offerIds, locale] deps are inert by design, not an oversight:
    // offerIds is a fresh array identity on every parent render regardless
    // of content, so this effect re-fires on nearly every render, not on a
    // meaningful change to either value. `sent` — not the dep array — is
    // what guarantees a single report per mount; a locale switch does NOT
    // trigger a second one.
    //
    // React 18 StrictMode double-invokes effects in development; without this
    // guard every dev session reports twice the real impressions.
    if (sent.current) return;
    sent.current = true;
    trackEvent({
      event: "hero_offers_rendered",
      offerIds,
      count: offerIds.length,
      // Derived here rather than passed in: the variant and the count are two
      // views of one fact, and a caller that could supply them separately
      // could supply them contradicting each other.
      variant: offerIds.length > 0 ? "offers" : "trust",
      locale,
    });
  }, [offerIds, locale]);
  return null;
}
